import React from 'react';
import API_BASE_URL from '../config/api';
import '../styles/BookingDetailsModal.css';

const BookingDetailsModal = ({ booking, onClose }) => {
  if (!booking) return null;

  const getUploadsBaseUrl = () => {
    let baseUrl = (API_BASE_URL || '').trim().replace(/\/+$/, '');
    const apiPath = (process.env.REACT_APP_API_BASE_PATH || '').trim().replace(/\/+$/, '');

    if (apiPath && baseUrl.endsWith(apiPath)) {
      baseUrl = baseUrl.slice(0, -apiPath.length).replace(/\/+$/, '');
    }

    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4).replace(/\/+$/, '');
    }

    return baseUrl || 'http://localhost:5001';
  };

  // Helper function to construct image URL if not provided by backend
  const getImageUrl = (imagePath, imageUrl, subdirectory) => {
    const uploadsBaseUrl = getUploadsBaseUrl();

    // If backend returns a full URL, use it but normalize known wrong frontend ports.
    if (imageUrl) {
      return imageUrl
        .replace(':3000', ':5001')
        .replace(':3001', ':5001')
        .replace('/api/uploads/', '/uploads/');
    }

    if (!imagePath) return null;

    let path = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    path = path.replace(/\\/g, '/');

    // Normalize possible persisted prefixes.
    if (path.startsWith('uploads/')) {
      path = path.slice('uploads/'.length);
    }

    // Add subdirectory if not present (legacy records may store only filename).
    if (!path.startsWith(`${subdirectory}/`)) {
      path = `${subdirectory}/${path}`;
    }

    return `${uploadsBaseUrl}/uploads/${path}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('sv-SE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price) + ' SEK';
  };

  // Helper function to clean and capitalize departure city name
  const formatDepartureCity = (city) => {
    if (!city) return '-';
    // Remove package ID suffix (e.g., "-4") and trim
    const cleaned = city.replace(/-\d+$/, '').trim();
    if (!cleaned) return '-';
    // Capitalize first letter, lowercase the rest
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
  };

  // Download image from URL and save with given filename
  const handleDownloadImage = async (imageUrl, filename) => {
    if (!imageUrl) return;
    try {
      const response = await fetch(imageUrl, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download image. Please try again.');
    }
  };

  // Safe filename from passenger name (alphanumeric and hyphens only)
  const getSafeFilename = (name, prefix, ext = 'jpg') => {
    const safe = (name || 'passenger').replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').trim() || 'passenger';
    return `${prefix}-${safe}.${ext}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Booking Details</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Booking Information */}
          <section className="details-section">
            <h3>Booking Information</h3>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">Reference:</span>
                <span className="detail-value">{booking.booking_reference}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Month:</span>
                <span className="detail-value">{booking.month}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Departure City:</span>
                <span className="detail-value">{formatDepartureCity(booking.departure_city)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Total Price:</span>
                <span className="detail-value price">{formatPrice(booking.total_price)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`detail-value status-badge status-${booking.status}`}>
                  {booking.status}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Payment Status:</span>
                <span className={`detail-value status-badge payment-${booking.payment_status}`}>
                  {booking.payment_status}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created At:</span>
                <span className="detail-value">{formatDate(booking.created_at)}</span>
              </div>
              {booking.payment_deadline && (
                <div className="detail-item">
                  <span className="detail-label">Payment Deadline:</span>
                  <span className="detail-value">{formatDate(booking.payment_deadline)}</span>
                </div>
              )}
            </div>
          </section>

          {/* Tickets Information */}
          {booking.tickets && (
            <section className="details-section">
              <h3>Tickets Breakdown</h3>
              <div className="tickets-grid">
                {booking.tickets.adult_4bed > 0 && (
                  <div className="ticket-item">
                    <span>Adult 4-bed:</span>
                    <strong>{booking.tickets.adult_4bed}</strong>
                  </div>
                )}
                {booking.tickets.adult_3bed > 0 && (
                  <div className="ticket-item">
                    <span>Adult 3-bed:</span>
                    <strong>{booking.tickets.adult_3bed}</strong>
                  </div>
                )}
                {booking.tickets.adult_2bed > 0 && (
                  <div className="ticket-item">
                    <span>Adult 2-bed:</span>
                    <strong>{booking.tickets.adult_2bed}</strong>
                  </div>
                )}
                {booking.tickets.child_4bed > 0 && (
                  <div className="ticket-item">
                    <span>Child 4-bed:</span>
                    <strong>{booking.tickets.child_4bed}</strong>
                  </div>
                )}
                {booking.tickets.child_3bed > 0 && (
                  <div className="ticket-item">
                    <span>Child 3-bed:</span>
                    <strong>{booking.tickets.child_3bed}</strong>
                  </div>
                )}
                {booking.tickets.child_2bed > 0 && (
                  <div className="ticket-item">
                    <span>Child 2-bed:</span>
                    <strong>{booking.tickets.child_2bed}</strong>
                  </div>
                )}
                {booking.tickets.infant > 0 && (
                  <div className="ticket-item">
                    <span>Infant:</span>
                    <strong>{booking.tickets.infant}</strong>
                  </div>
                )}
                {booking.tickets.total_tickets && (
                  <div className="ticket-item total">
                    <span>Total Tickets:</span>
                    <strong>{booking.tickets.total_tickets}</strong>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Passengers Information */}
          {booking.passengers && booking.passengers.length > 0 && (
            <section className="details-section">
              <h3>Passengers ({booking.passengers.length})</h3>
              <div className="passengers-list">
                {booking.passengers.map((passenger, index) => (
                  <div key={passenger.id || index} className="passenger-card">
                    <div className="passenger-header">
                      <h4>Passenger {index + 1}</h4>
                      <span className="gender-badge">{passenger.gender}</span>
                    </div>
                    <div className="passenger-details">
                      <div className="passenger-row">
                        <span className="label">Full Name:</span>
                        <span className="value">{passenger.full_name}</span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Date of Birth:</span>
                        <span className="value">{formatDate(passenger.date_of_birth)}</span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Email:</span>
                        <span className="value">{passenger.email}</span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Phone:</span>
                        <span className="value">{passenger.phone_number}</span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Address:</span>
                        <span className="value">
                          {passenger.address}, {passenger.zip_code} {passenger.city}
                        </span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Passport Number:</span>
                        <span className="value">{passenger.passport_number}</span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Passport Country:</span>
                        <span className="value">{passenger.passport_country}</span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Passport Issue Date:</span>
                        <span className="value">{formatDate(passenger.passport_issue_date)}</span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Passport Expiry:</span>
                        <span className="value">{formatDate(passenger.passport_expiration_date)}</span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Departure City:</span>
                        <span className="value">{formatDepartureCity(passenger.departure_city)}</span>
                      </div>
                      {passenger.room_share_preference && (
                        <div className="passenger-row">
                          <span className="label">Room Share Preference:</span>
                          <span className="value">{passenger.room_share_preference}</span>
                        </div>
                      )}
                      <div className="passenger-row">
                        <span className="label">Emergency Contact:</span>
                        <span className="value">
                          {passenger.emergency_contact_name} ({passenger.emergency_contact_relationship})
                        </span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Emergency Phone:</span>
                        <span className="value">{passenger.emergency_contact_number}</span>
                      </div>
                      <div className="passenger-row">
                        <span className="label">Previous Hajj/Umrah:</span>
                        <span className="value capitalize">{passenger.previous_hajj_umrah}</span>
                      </div>
                      {passenger.special_needs && (
                        <div className="passenger-row">
                          <span className="label">Special Needs:</span>
                          <span className="value">{passenger.special_needs}</span>
                        </div>
                      )}
                      {passenger.social_security_number && (
                        <div className="passenger-row">
                          <span className="label">Social Security Number:</span>
                          <span className="value">{passenger.social_security_number}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Passenger Images */}
                    {(passenger.facial_image_path || passenger.facial_image_url || passenger.passport_photo_path || passenger.passport_photo_url) && (
                      <div className="passenger-images">
                        {(passenger.facial_image_url || passenger.facial_image_path) && (() => {
                          const facialImageUrl = getImageUrl(passenger.facial_image_path, passenger.facial_image_url, 'facial-images');
                          return (
                            <div className="passenger-image-container">
                              <span className="label">Facial Image:</span>
                              {facialImageUrl ? (
                                <>
                                  <img 
                                    src={encodeURI(facialImageUrl)} 
                                    alt="Facial" 
                                    className="passenger-image"
                                    onError={(e) => {
                                      console.error('Failed to load facial image:', facialImageUrl);
                                      e.target.style.display = 'none';
                                      const errorSpan = e.target.nextElementSibling;
                                      if (errorSpan) {
                                        errorSpan.style.display = 'block';
                                      }
                                    }}
                                    onLoad={() => console.log('Facial image loaded successfully:', facialImageUrl)}
                                  />
                                  <button
                                    type="button"
                                    className="image-download-btn"
                                    onClick={() => handleDownloadImage(facialImageUrl, getSafeFilename(passenger.full_name, 'facial'))}
                                  >
                                    Download facial image
                                  </button>
                                </>
                              ) : (
                                <div className="image-placeholder">Image URL not available</div>
                              )}
                              <span className="image-error" style={{ display: 'none' }}>Image not available</span>
                            </div>
                          );
                        })()}
                        {(passenger.passport_photo_url || passenger.passport_photo_path) && (() => {
                          const passportImageUrl = getImageUrl(passenger.passport_photo_path, passenger.passport_photo_url, 'passport-photos');
                          return (
                            <div className="passenger-image-container">
                              <span className="label">Passport Photo:</span>
                              {passportImageUrl ? (
                                <>
                                  <img 
                                    src={encodeURI(passportImageUrl)} 
                                    alt="Passport" 
                                    className="passenger-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const errorSpan = e.target.nextElementSibling;
                                      if (errorSpan) {
                                        errorSpan.style.display = 'block';
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="image-download-btn"
                                    onClick={() => handleDownloadImage(passportImageUrl, getSafeFilename(passenger.full_name, 'passport'))}
                                  >
                                    Download passport image
                                  </button>
                                </>
                              ) : (
                                <div className="image-placeholder">Image URL not available</div>
                              )}
                              <span className="image-error" style={{ display: 'none' }}>Image not available</span>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="modal-footer">
          <button className="close-button-footer" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailsModal;
